import React from 'react';
import { Link } from 'react-router-dom';
import { MyUnitCodeDto } from '../../types/unit';
import { BookOpen, Clock, Users, Play, Sparkles, GraduationCap } from 'lucide-react';

interface UnitCardProps {
  unit: MyUnitCodeDto;
}

const UnitCard: React.FC<UnitCardProps> = ({ unit }) => {
  const getGradeInArabic = (grade: string) => {
    switch (grade) {
      case 'Secondary1': return 'الصف الأول الثانوي';
      case 'Secondary2': return 'الصف الثاني الثانوي';
      case 'Secondary3': return 'الصف الثالث الثانوي';
      default: return grade;
    }
  };

  const getTermInArabic = (term: string) => {
    switch (term) {
      case 'First': return 'الترم الأول';
      case 'Second': return 'الترم الثاني';
      default: return term;
    }
  };

  return (
    <div className="group relative">
      {/* Outer glow effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/20 via-teal-400/20 to-cyan-400/20 rounded-xl blur-xl group-hover:blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
      
      {/* Main card */}
      <div className="relative bg-white/80 backdrop-blur-xl rounded-xl border border-white/30 shadow-md group-hover:shadow-lg group-hover:shadow-emerald-500/20 transition-all duration-500 overflow-hidden transform group-hover:-translate-y-1">
        {/* Inner gradient border */}
        <div className="absolute inset-[1px] rounded-xl bg-gradient-to-br from-white/20 to-transparent pointer-events-none"></div>
        
        {/* Header Section */}
        <div className="relative p-3 bg-gradient-to-br from-emerald-50 to-teal-50">
          {/* Status badge */}
          <div className="absolute top-2 left-2 flex items-center gap-1">
            <div className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
              unit.active 
                ? 'bg-green-100 text-green-700 border-green-200' 
                : 'bg-gray-100 text-gray-600 border-gray-200'
            }`}>
              {unit.active ? 'نشط' : 'غير نشط'}
            </div>
          </div>
          
          {/* Grade and Term badges */}
          <div className="absolute top-2 right-2 flex flex-col gap-1">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold shadow-md">
              {getGradeInArabic(unit.grade)}
            </div>
            <div className="bg-white/70 backdrop-blur-sm text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-medium border border-emerald-200">
              {getTermInArabic(unit.term)}
            </div>
          </div>
          
          {/* Unit icon */}
          <div className="w-10 h-10 mx-auto bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg mb-2 group-hover:scale-110 transition-transform duration-300">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
        </div>
        
        {/* Content Section */}
        <div className="p-3 relative">
          {/* Unit title */}
          <h3 className="text-sm font-bold text-gray-900 mb-1.5 text-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-teal-600 group-hover:bg-clip-text transition-all duration-300 line-clamp-2">
            {unit.unitName}
          </h3>
          
          {/* Unit subtitle */}
         
          
          {/* Course info */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-2 mb-3 border border-emerald-100">
            <div className="flex items-center gap-1.5 text-emerald-700">
              <GraduationCap className="w-3 h-3" />
              <span className="text-xs font-medium line-clamp-1">{unit.courseName}</span>
            </div>
          </div>
          
          {/* Action button */}
          <div className="text-center">
            <Link 
              to={`/unit-viewer/${unit.unitId}`}
              className="group/btn relative inline-flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold text-xs shadow-md hover:shadow-lg hover:shadow-emerald-500/50 transition-all duration-300 transform hover:scale-105 overflow-hidden"
            >
              <span className="relative z-10">دخول المحاضرة</span>
              <Play className="w-3 h-3 relative z-10 transform group-hover/btn:translate-x-1 transition-transform" />
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-700 to-teal-700 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
            </Link>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-1/3 -left-4 w-5 h-5 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-md shadow-md flex items-center justify-center animate-pulse opacity-60">
          <Sparkles className="w-3 h-3 text-white" />
        </div>
        
        <div className="absolute bottom-1/3 -right-4 w-4 h-4 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-full shadow-md animate-pulse opacity-60"></div>
        
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 pointer-events-none"></div>
      </div>
    </div>
  );
};

export default React.memo(UnitCard); 